/**
 * "Signal Storm" — original hard trigger level (difficulty 8/10).
 * Pulse Works machinery under pressure: a late-lifting gate, a bridge that
 * reveals at the last beat, a glitch-drenched wall lift, a slow-motion spike
 * chain, then a 3× sprint out. Authored in block coordinates → SSL LevelData.
 */
import type { LevelData, LevelObject } from '../../format/types';
import { UNITS_PER_BLOCK as B } from '../../engine/physics/constants';

let nextId = 1;
const objects: LevelObject[] = [];

function cell(bx: number, by: number): { x: number; y: number } {
  return { x: bx * B + B / 2, y: by * B + B / 2 };
}

function push(o: Omit<LevelObject, 'id'>): void {
  objects.push({ id: nextId++, ...o });
}

const spike = (bx: number, by = 0): void => push({ type: 'spike', ...cell(bx, by) });
const block = (bx: number, by = 0, extra: Partial<LevelObject> = {}): void =>
  push({ type: 'block', ...cell(bx, by), ...extra });
const portal = (bx: number, kind: string, by = 1): void =>
  push({ type: 'portal', ...cell(bx, by), props: { kind } });
const trig = (bx: number, props: Record<string, number | string | boolean>): void =>
  push({ type: 'trigger', ...cell(bx, 1), props });

// --- Opening beats (0–36) -------------------------------------------------------
spike(20);
spike(28);
spike(29);

// --- The late gate (36–52) ------------------------------------------------------
// Wall lifts only 1.0s before arrival at 1× speed — no slack.
block(40, 0, { groups: [1], colorChannel: 1 });
block(40, 1, { groups: [1], colorChannel: 1 });
block(40, 2, { groups: [1], colorChannel: 1 });
trig(30, { kind: 'move', target: 1, dx: 0, dy: 5, duration: 1.0, easing: 'inout' });
trig(30.4, { kind: 'pulse', channel: 1, hex: '#ffffff', duration: 0.5 });
spike(48);

// --- Last-beat bridge (52–70) ---------------------------------------------------
for (let bx = 54; bx <= 64; bx++) spike(bx);
for (let bx = 53; bx <= 65; bx += 2) block(bx, 1, { groups: [2], colorChannel: 2 });
trig(2, { kind: 'toggle', target: 2, visible: false });
trig(46, { kind: 'toggle', target: 2, visible: true });
trig(46.5, { kind: 'pulse', channel: 2, hex: '#a3e635', duration: 0.4 });

// --- 2× band with a triple (70–104) ----------------------------------------------
portal(70, 'speed:two');
spike(78);
spike(79);
spike(88);
spike(89);
spike(98);
spike(99);
spike(100);

// --- Glitch wall (104–126) --------------------------------------------------------
trig(104, { kind: 'camera:zoom', zoom: 1.3, duration: 0.8, easing: 'inout' });
trig(106, { kind: 'shader:glitch', strength: 0.5, duration: 0.8 });
block(116, 0, { groups: [3], colorChannel: 3 });
block(116, 1, { groups: [3], colorChannel: 3 });
block(116, 2, { groups: [3], colorChannel: 3 });
trig(106.5, { kind: 'move', target: 3, dx: 0, dy: 5, duration: 0.9, easing: 'out' });
spike(122);

// --- Slow-motion chain (126–152) --------------------------------------------------
trig(126, { kind: 'timewarp', factor: 0.75 });
trig(127, { kind: 'shader:chromatic', strength: 0.6, duration: 0.5 });
spike(132);
spike(133);
spike(140);
spike(141);
trig(148, { kind: 'timewarp', factor: 1 });
trig(148.5, { kind: 'shader:chromatic', strength: 0, duration: 0.5 });

// --- 3× sprint out (152–200) -------------------------------------------------------
portal(152, 'speed:three');
trig(152.5, { kind: 'camera:zoom', zoom: 1, duration: 0.8, easing: 'inout' });
spike(164);
spike(165);
spike(178);
spike(179);
spike(190);

// Secret shards.
push({ type: 'collectible', ...cell(44, 2), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(94, 2), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(171, 2), props: { kind: 'shard' } });

push({ type: 'endgate', ...cell(200, 2) });

export const SIGNAL_STORM: LevelData = {
  formatVersion: 1,
  meta: {
    name: 'Signal Storm',
    author: 'Shape Shifters Team',
    description: 'The machinery fights back.',
    revision: 1,
    createdAt: '2026-07-24T00:00:00.000Z',
    updatedAt: '2026-07-24T00:00:00.000Z',
    difficulty: 8,
    tags: ['triggers', 'official'],
  },
  song: { id: 'circuit-breaker', bpm: 140, offsetMs: 0 },
  settings: {
    mode: 'normal',
    speed: 'one',
    startForm: 'runner',
    accentColor: '#fbbf24',
    hazardColor: '#f43f5e',
    backgroundColor: '#170d02',
  },
  objects,
};
